const API_BASE = '/api/compliance/training';

const getCsrfToken = () => document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/)?.[1] ?? '';

function getAuthHeaders(): HeadersInit {
  const token = sessionStorage.getItem('impersonation_token') || localStorage.getItem('token');
  if (!token) throw new Error('No authentication token found');
  return { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'X-CSRF-Token': getCsrfToken() };
}

async function handleResponse<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const err = await res.json().catch(() => ({ message: 'Unknown error' }));
    throw new Error(err.error || err.message || `API Error: ${res.status}`);
  }
  return res.json();
}

export interface TrainingQuestion {
  id: number;
  module_id: number;
  question: string;
  options: string[];
  correct_answer: number;
  explanation?: string;
  order_index: number;
}

export interface TrainingModule {
  id: number;
  company_id: number;
  title: string;
  category: string;
  description?: string;
  content?: string;
  duration_minutes: number;
  passing_score: number;
  is_mandatory: boolean;
  is_active: boolean;
  version: number;
  questions?: TrainingQuestion[];
  completion_rate?: number;
  assigned_count?: number;
  created_at: string;
  updated_at?: string;
}

export interface TrainingAssignment {
  id: number;
  module_id: number;
  module_title: string;
  module_category: string;
  module_duration: number;
  module_content?: string;
  is_mandatory: boolean;
  user_id: number;
  assigned_by?: number;
  due_date?: string;
  status: 'assigned' | 'in_progress' | 'completed' | 'overdue' | 'failed' | 'exempted';
  score?: number;
  completed_at?: string;
  created_at: string;
}

export interface TrainingCompletion {
  id: number;
  assignment_id: number;
  user_id: number;
  module_id: number;
  score: number;
  passed: boolean;
  answers?: Record<number, number>;
  time_taken_minutes?: number;
  completed_at: string;
}

export interface TrainingStats {
  total_modules: number;
  active_modules: number;
  total_assignments: number;
  completed: number;
  overdue: number;
  passed: number;
  failed: number;
  completion_rate: number;
  average_score: number;
}

export const trainingApi = {
  async getModules(): Promise<{ modules: TrainingModule[]; total: number }> {
    const res = await fetch(`${API_BASE}/modules`, { headers: getAuthHeaders() });
    return handleResponse(res);
  },

  async getModule(id: number): Promise<TrainingModule> {
    const res = await fetch(`${API_BASE}/modules/${id}`, { headers: getAuthHeaders() });
    const result: any = await handleResponse(res);
    return result.data || result;
  },

  async createModule(data: Partial<TrainingModule>): Promise<TrainingModule> {
    const res = await fetch(`${API_BASE}/modules`, { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(data) });
    const result: any = await handleResponse(res);
    return result.data || result;
  },

  async updateModule(id: number, data: Partial<TrainingModule>): Promise<TrainingModule> {
    const res = await fetch(`${API_BASE}/modules/${id}`, { method: 'PUT', headers: getAuthHeaders(), body: JSON.stringify(data) });
    const result: any = await handleResponse(res);
    return result.data || result;
  },

  async deleteModule(id: number): Promise<void> {
    const res = await fetch(`${API_BASE}/modules/${id}`, { method: 'DELETE', headers: getAuthHeaders() });
    await handleResponse(res);
  },

  async addQuestion(moduleId: number, data: { question: string; options: string[]; correct_answer: number; explanation?: string }): Promise<TrainingQuestion> {
    const res = await fetch(`${API_BASE}/modules/${moduleId}/questions`, { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(data) });
    const result: any = await handleResponse(res);
    return result.data || result;
  },

  async deleteQuestion(moduleId: number, questionId: number): Promise<void> {
    const res = await fetch(`${API_BASE}/modules/${moduleId}/questions/${questionId}`, { method: 'DELETE', headers: getAuthHeaders() });
    await handleResponse(res);
  },

  async assignModule(moduleId: number, data: { user_ids?: number[]; assign_all?: boolean; due_date?: string }): Promise<{ assigned: number }> {
    const res = await fetch(`${API_BASE}/modules/${moduleId}/assign`, { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(data) });
    const result: any = await handleResponse(res);
    return result.data || result;
  },

  async getMyAssignments(): Promise<{ assignments: TrainingAssignment[]; total: number }> {
    const res = await fetch(`${API_BASE}/assignments/my`, { headers: getAuthHeaders() });
    return handleResponse(res);
  },

  async getCompanyAssignments(params?: { status?: string; module_id?: number; user_id?: number; page?: number; limit?: number }): Promise<{ assignments: TrainingAssignment[]; total: number }> {
    const q = new URLSearchParams();
    if (params?.status) q.set('status', params.status);
    if (params?.module_id) q.set('module_id', String(params.module_id));
    if (params?.user_id) q.set('user_id', String(params.user_id));
    if (params?.page) q.set('page', String(params.page));
    if (params?.limit) q.set('limit', String(params.limit));
    const res = await fetch(`${API_BASE}/assignments?${q}`, { headers: getAuthHeaders() });
    return handleResponse(res);
  },

  async submitCompletion(assignmentId: number, data: { answers: Record<number, number>; time_taken_minutes?: number }): Promise<{ passed: boolean; score: number; passing_score: number }> {
    const res = await fetch(`${API_BASE}/assignments/${assignmentId}/complete`, { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(data) });
    const result: any = await handleResponse(res);
    return result.data || result;
  },

  async getStats(): Promise<TrainingStats> {
    const res = await fetch(`${API_BASE}/stats`, { headers: getAuthHeaders() });
    const result: any = await handleResponse(res);
    return result.data || result;
  },

  async getUserProgress(userId: number): Promise<{ assignments: TrainingAssignment[]; stats: TrainingStats }> {
    const res = await fetch(`${API_BASE}/progress/${userId}`, { headers: getAuthHeaders() });
    const result: any = await handleResponse(res);
    return result.data || result;
  },
};
